import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../lib/utils';
import { useAppSelector } from '../store';
import type { C2Event } from '../types';

interface EventContextValue {
  activeEvent: C2Event | null;
  activeEventId: string | null;
  loading: boolean;
  refreshActiveEvent: () => Promise<void>;
  setActiveEventLocal: (event: C2Event | null) => void;
}

const EventContext = createContext<EventContextValue>({
  activeEvent: null,
  activeEventId: null,
  loading: false,
  refreshActiveEvent: async () => {},
  setActiveEventLocal: () => {},
});

export function useActiveEvent() {
  return useContext(EventContext);
}

export function EventProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const { token } = useAppSelector((state) => state.auth);
  const [activeEvent, setActiveEvent] = useState<C2Event | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchGenRef = useRef(0);

  const eventId = searchParams.get('eventId');

  // Persist eventId to sessionStorage to survive window resize/move
  useEffect(() => {
    if (eventId) {
      sessionStorage.setItem('c2_active_event_id', eventId);
    }
  }, [eventId]);

  // Fallback to sessionStorage if URL param is lost
  const effectiveEventId = eventId || sessionStorage.getItem('c2_active_event_id');

  useLayoutEffect(() => {
    if (effectiveEventId && token) {
      setLoading(true);
    } else if (!effectiveEventId) {
      setLoading(false);
      setActiveEvent(null);
    }
  }, [effectiveEventId, token]);

  const refreshActiveEvent = async () => {
    if (!effectiveEventId) {
      setActiveEvent(null);
      setLoading(false);
      return;
    }

    if (!token) {
      return;
    }

    const gen = ++fetchGenRef.current;
    try {
      const r = await fetch(`${API_BASE_URL}/events/${effectiveEventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (gen !== fetchGenRef.current) return;
      setActiveEvent(d.event || null);
    } catch {
      if (gen !== fetchGenRef.current) return;
      setActiveEvent(null);
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    refreshActiveEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveEventId, token]);

  return (
    <EventContext.Provider
      value={{
        activeEvent,
        activeEventId: effectiveEventId,
        loading,
        refreshActiveEvent,
        setActiveEventLocal: setActiveEvent,
      }}
    >
      {children}
    </EventContext.Provider>
  );
}
